import { MagnifyingGlassIcon, StarIcon, TagIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ICON_BUTTON_SIZE } from '../components/touch-target';
import type { TagMode } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from './format';

/**
 * Puces EFFAÇABLES des critères de RECHERCHE actifs (nom de fichier / note min / tags),
 * dans l'esprit de la puce de filtre EXIF. Chaque puce porte une icône + un texte
 * (jamais la couleur seule) et une croix qui retire CE critère (les autres restent).
 * Le filtre par TYPE n'a plus qu'UNE source (menu « Trier & filtrer ») → pas de puce ici.
 */

interface AdvancedFilterChipsProps {
  /** Recherche par nom de fichier active (`q`) — chaîne vide = pas de puce. */
  filename: string;
  ratingMin: number | null;
  tagIds: string[];
  tagMode: TagMode;
  onClearFilename: () => void;
  onClearRating: () => void;
  onClearTags: () => void;
}

export function AdvancedFilterChips({
  filename,
  ratingMin,
  tagIds,
  tagMode,
  onClearFilename,
  onClearRating,
  onClearTags,
}: AdvancedFilterChipsProps): React.JSX.Element | null {
  const { t, locale } = useI18n();

  const q = filename.trim();
  const filenameActive = q !== '';
  const ratingActive = ratingMin !== null && ratingMin > 0;
  const tagsActive = tagIds.length > 0;
  if (!filenameActive && !ratingActive && !tagsActive) return null;

  return (
    <div
      role="group"
      aria-label={t('advancedSearch.chipsLabel')}
      className="flex flex-wrap items-center gap-2"
    >
      {filenameActive && (
        <Chip
          icon={<MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />}
          label={interpolate(t('advancedSearch.chipFilename'), { q })}
          removeLabel={t('advancedSearch.removeFilename')}
          onRemove={onClearFilename}
        />
      )}
      {ratingActive && (
        <Chip
          icon={<StarIcon className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />}
          label={interpolate(t('advancedSearch.chipRating'), { rating: ratingMin })}
          removeLabel={t('advancedSearch.removeRating')}
          onRemove={onClearRating}
        />
      )}
      {tagsActive && (
        <Chip
          icon={<TagIcon className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />}
          label={interpolate(
            t(tagMode === 'all' ? 'advancedSearch.chipTagsAll' : 'advancedSearch.chipTagsAny'),
            { count: tagIds.length },
            locale,
          )}
          removeLabel={t('advancedSearch.removeTags')}
          onRemove={onClearTags}
        />
      )}
    </div>
  );
}

function Chip({
  icon,
  label,
  removeLabel,
  onRemove,
}: {
  icon: React.ReactNode;
  label: string;
  removeLabel: string;
  onRemove: () => void;
}): React.JSX.Element {
  return (
    <span className="flex items-center gap-1.5 rounded-md border border-border bg-surface-2 px-2 py-1 text-sm text-fg">
      {icon}
      <span>{label}</span>
      <button
        type="button"
        onClick={onRemove}
        title={removeLabel}
        aria-label={removeLabel}
        className={`${ICON_BUTTON_SIZE} rounded-md text-muted hover:bg-surface hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand`}
      >
        <XMarkIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
      </button>
    </span>
  );
}
